import { Box, CircularProgress } from '@mui/material';
import { useUnmountedRef } from 'ahooks';
import { drawRenderables } from 'boardCanvas/CanvasRenderer';
import { getMaterialImagePromise } from 'boardCanvas/Materials';
import { MutableRefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { BoardType, Dimensions2D, Material, Renderable } from 'Types';

import { scaleDimensions } from 'utils/utils';

function BoardCanvas<T extends BoardType>(props: {
  canvasRef?: MutableRefObject<HTMLCanvasElement | null>;
  dimensions: Dimensions2D;
  renderables: Array<Renderable<T>>;
  scaleFactor: number;
}) {
  const { dimensions, renderables, scaleFactor } = props;

  const scaledDimensions = scaleDimensions(dimensions, scaleFactor);

  const fallbackCanasRef = useRef<HTMLCanvasElement | null>(null)
  const canvasRef = props.canvasRef ?? fallbackCanasRef

  const unmountedRef = useUnmountedRef();

  const [areImagesLoaded, setAreImagesLoaded] = useState(false);

  const update = useCallback(() => {
    let cancel = false;

    const usedMaterials = new Set<Material>();
    for (const { material } of renderables) {
      if (material != null) {
        usedMaterials.add(material);
      }
    }
    const materialImagePromise = Promise.all(
      Array.from(usedMaterials).map(getMaterialImagePromise)
    );

    materialImagePromise.then(() => {
      if (unmountedRef.current) {
        return;
      }

      setAreImagesLoaded(true);
      if (!cancel && canvasRef.current) {
        canvasRef.current.height = scaledDimensions.height;
        canvasRef.current.width = scaledDimensions.width;
        drawRenderables(canvasRef.current, renderables, scaleFactor);
      }
    });

    return () => {
      cancel = true;
    };
  }, [
    canvasRef,
    renderables,
    scaledDimensions.height,
    scaledDimensions.width,
    scaleFactor,
    unmountedRef,
  ]);

  useLayoutEffect(update, [
    renderables,
    scaledDimensions.height,
    scaledDimensions.width,
    scaleFactor,
    update,
  ]);

  return (
    <Box sx={{ display: 'flex' }}>
      {areImagesLoaded ? (
        <canvas
          ref={(ref) => {
            canvasRef.current = ref;
            update();
          }}
        />
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
}

export default BoardCanvas;
